import { Shield, Pencil } from 'lucide-react';
import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";


import { useProviderCompany } from "../../../../../context/ProviderCompanyContext";
import { updateProviderRecommendationPreferences } from '../../../../../services/staffingService';

const officerSchema = yup.object().shape({
  distance: yup.number().min(0).required("Distance is required"),
});

const OfficerRecommendations = ({providerCompanyId}) => {
  const { providerCompanyData, fetchProviderCompanyData } = useProviderCompany();
  
  // State to handle whether we are in editing mode or not
  const [isEditing, setIsEditing] = useState(false);
  const [ranking, setRanking] = useState([]);

  // React Hook Form setup with yup resolver
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    reset
  } = useForm({
    resolver: yupResolver(officerSchema),
    defaultValues: {
      distance: 0, // Initial default value, will be updated from providerCompanyData
    },
  });

  // UseEffect to update form when providerCompanyData changes
  useEffect(() => {
    if (providerCompanyData && providerCompanyData.preferences?.recommendations) {
      // Populate the distance from providerCompanyData
      reset({
        distance: providerCompanyData.preferences?.recommendations.showOfficersWithinMiles || 0,
      });

      // Initialize ranking based on providerCompanyData or default values
      setRanking([
        { id: "1", content: "Highest margin" },
        { id: "2", content: "With same customer" },
        { id: "3", content: "Good reviews" }
      ]);
    }
  }, [providerCompanyData, reset]);

  // Move up in ranking
  const moveUp = (index) => {
    if (index > 0) {
      const newRanking = [...ranking];
      [newRanking[index - 1], newRanking[index]] = [
        newRanking[index],
        newRanking[index - 1],
      ];
      setRanking(newRanking);
    }
  };

  // Move down in ranking
  const moveDown = (index) => {
    if (index < ranking.length - 1) {
      const newRanking = [...ranking];
      [newRanking[index + 1], newRanking[index]] = [
        newRanking[index],
        newRanking[index + 1],
      ];
      setRanking(newRanking);
    }
  };

  // Function to handle form submission
  const onSubmit = async (data) => {
    try {
      const recommendationData = {
        showOfficersWithinMiles: data.distance,
        highestMargin: ranking.findIndex(item => item.content === "Highest margin") + 1,
        withSameCustomer: ranking.findIndex(item => item.content === "With same customer") + 1,
        goodReviews: ranking.findIndex(item => item.content === "Good reviews") + 1,
      };

      // Update the backend with the new recommendation data
      await updateProviderRecommendationPreferences(providerCompanyData._id, recommendationData);

      // Fetch updated provider company data to refresh context
      fetchProviderCompanyData(providerCompanyId);

      // Switch back to view mode
      setIsEditing(false);

    } catch (error) {
      console.error("Failed to update officer recommendations:", error);
    }
  };

  // Toggle edit mode
  const toggleEditMode = () => {
    setIsEditing(!isEditing);
    if (!isEditing && providerCompanyData?.preferences?.recommendations) {
      // Prepopulate the form when switching to edit mode
      setValue("distance", providerCompanyData.preferences.recommendations.showOfficersWithinMiles || 0);
    }
  };

  // RankItem component to display each ranking item
  const RankItem = ({ item, index, moveUp, moveDown, totalItems }) => {
    return (
      <li className="p-2 my-1 bg-gray-100 border border-gray-300 rounded flex items-center justify-between">
        <span className="text-sm">{item.content}</span>
        <div className="flex gap-1">
          <button
            onClick={() => moveUp(index)}
            disabled={index === 0}
            className="px-2 py-1 text-sm bg-blue-500 text-white rounded disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            ↑
          </button>
          <button
            onClick={() => moveDown(index)}
            disabled={index === totalItems - 1}
            className="px-2 py-1 text-sm bg-blue-500 text-white rounded disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            ↓
          </button>
        </div>
      </li>
    );
  };

  return (
    <div className="flex items-start bg-white rounded-lg shadow-sm p-4 mb-4">
      <Shield className="text-green-500 mr-5 mt-1" style={{ fontSize: "20px" }} />
      <div className="flex-1">
        {/* Title with Edit button */}
        <h6 className="mb-1 text-sm font-semibold text-brand-blue-dark flex items-center">
          Officer Recommendations
          {!isEditing && (
            <Pencil
              onClick={toggleEditMode}
              className="ml-2 w-4 h-4 cursor-pointer text-gray-500 hover:text-gray-700"
            />
          )}
        </h6>

        {/* Display mode or Edit mode */}
        {isEditing ? (
          <div className="mt-4">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="w-full">
                <div className="space-y-4">
                  <div>
                    <label className="form-label">
                      Show officers within (miles):
                    </label>
                    <input
                      className="form-input"
                      type="number"
                      {...register("distance")}
                    />
                    {errors.distance && (
                      <span className="form-error">
                        {errors.distance?.message}
                      </span>
                    )}
                  </div>

                  <div>
                    <label className="form-label">Adjust Ranking:</label>
                    <ul className="list-none p-0 space-y-1">
                      {ranking.map((item, index) => (
                        <RankItem
                          key={item.id}
                          index={index}
                          item={item}
                          moveUp={moveUp}
                          moveDown={moveDown}
                          totalItems={ranking.length}
                        />
                      ))}
                    </ul>
                  </div>

                  <div className="flex gap-2">
                    <button className="px-4 py-2 bg-brand-blue-dark text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity" type="submit">
                      Save
                    </button>
                    <button
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors"
                      type="button"
                      onClick={() => setIsEditing(false)} // Cancel button functionality
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        ) : (
          <div>
            <p className="mb-2 text-sm text-gray-600">
              Show officer recommendations within {providerCompanyData?.preferences?.recommendations?.showOfficersWithinMiles || 0} miles
            </p>
            <p className="mb-2 text-sm text-gray-600">
              Recommendations Sections Ranking -
              {ranking.map((item) => item.content).join(", ")}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default OfficerRecommendations;
